import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, ShoppingBag, ChefHat, Store as StoreIcon, Package,
  Truck, Users, Clock, BarChart3, UserCircle2, UtensilsCrossed,
  ArrowLeft, LogOut, Home, ListChecks, Star, QrCode, GraduationCap, FileText, ChevronDown, MapPin, Menu, X,
} from "lucide-react";

export default function StoreLayout() {
  const { sid } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stores, setStores] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isSuper = user?.role === "super_admin";
  const isAdmin = isSuper || user?.role === "admin";
  const isManager = user?.role === "manager";
  const perms = user?.permissions || {};
  const myStoreIds = user?.assigned_store_ids || [];
  const isStaff = !isAdmin && !isManager;

  useEffect(() => {
    api.get("/stores").then((r) => setStores(isAdmin ? r.data : r.data.filter((s) => myStoreIds.includes(s.id))));
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  const store = stores.find((x) => x.id === sid) || null;
  const canSwitch = isAdmin || stores.length > 1;

  const can = (key, minVerb) => {
    if (isSuper) return true;
    const order = ["none", "own", "view", "create", "edit", "approve", "manage"];
    return order.indexOf(perms[key] || "none") >= order.indexOf(minVerb);
  };

  const NAV = useMemo(() => [
    { to: `/admin/stores/${sid}`,           end: true, icon: LayoutDashboard, label: "Overview",       show: true },
    { to: `/admin/stores/${sid}/inventory`,            icon: Package,        label: "Inventory",      show: can("inventory", "view") },
    { to: `/admin/stores/${sid}/restock`,              icon: Truck,          label: "Restock Requests", show: can("restock", "view") },
    { to: `/admin/stores/${sid}/tasks`,                icon: ListChecks,     label: "My Tasks",       show: isStaff },
    { to: `/admin/stores/${sid}/training`,             icon: GraduationCap,  label: "Training",       show: isStaff },
    { to: `/admin/stores/${sid}/documents`,            icon: FileText,       label: "My Documents",   show: isStaff },
    { to: `/admin/stores/${sid}/time-clock`,           icon: Clock,          label: "Time Clock",     show: isStaff },
    { to: `/admin/stores/${sid}/employees`,            icon: Users,          label: "Employees",      show: can("employees", "view") },
    { to: `/admin/stores/${sid}/orders`,               icon: ShoppingBag,    label: "Orders",         show: can("orders", "view") },
    { to: `/admin/stores/${sid}/kitchen`,              icon: ChefHat,        label: "Kitchen",        show: can("orders", "view") },
    { to: `/admin/stores/${sid}/pos`,                  icon: StoreIcon,      label: "POS",            show: can("pos", "view") },
    { to: `/admin/stores/${sid}/sales`,                icon: BarChart3,      label: "Sales & Reports", show: can("sales", "view") },
    { to: `/admin/stores/${sid}/customers`,            icon: UserCircle2,    label: "Customers",      show: can("customers", "view") },
    { to: `/admin/stores/${sid}/feedback`,             icon: Star,           label: "Feedback",       show: can("feedback", "view") },
    { to: `/admin/stores/${sid}/qr`,                   icon: QrCode,         label: "QR Codes",       show: can("qr", "view") },
    { to: `/admin/stores/${sid}/menu`,                 icon: UtensilsCrossed, label: "Menu & Pricing", show: can("menu", "view") },
  ].filter((n) => n.show), [sid, perms, isSuper, isStaff]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      <aside className={`${menuOpen ? "flex" : "hidden"} lg:flex fixed lg:static inset-0 z-40 w-full lg:w-60 bg-neutral-950 text-neutral-300 flex-col shrink-0`}>
        <div className="px-4 py-4 border-b border-neutral-900">
          <div className="flex items-center justify-between mb-3">
            {isAdmin ? (
              <button onClick={() => nav("/admin")} className="flex items-center gap-2 text-xs text-neutral-400 hover:text-white" data-testid="back-all-stores">
                <ArrowLeft className="w-3 h-3"/> All Stores
              </button>
            ) : <span/>}
            <button onClick={() => setMenuOpen(false)} className="lg:hidden text-neutral-400"><X className="w-5 h-5"/></button>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[hsl(var(--primary))] flex items-center justify-center text-white font-display font-bold text-sm">
              {(store?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-white leading-tight truncate" data-testid="store-name-header">{store?.name || "Store"}</div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 truncate">{store?.type || ""}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, end, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-900 hover:text-white"
                }`}
              data-testid={`store-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon className="w-4 h-4"/> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-900 space-y-1">
          <button onClick={() => nav("/")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-neutral-900 hover:text-white">
            <Home className="w-4 h-4"/> Back to Site
          </button>
          <div className="px-3 py-2 text-xs text-neutral-500">
            <div className="font-semibold text-neutral-300">{user?.name}</div>
            <div className="capitalize">{user?.role?.replace("_", " ")}</div>
          </div>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-neutral-900 hover:text-red-400" data-testid="store-logout">
            <LogOut className="w-4 h-4"/> Logout
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-neutral-200 px-4 lg:px-10 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setMenuOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-neutral-100" data-testid="store-menu-toggle"><Menu className="w-5 h-5"/></button>
            <div className="relative">
              <button onClick={() => canSwitch && setSwitcherOpen((o) => !o)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 text-sm font-medium" data-testid="location-selector">
                <MapPin className="w-3.5 h-3.5 text-[hsl(var(--primary))]"/>
                <span className="text-neutral-500 font-normal hidden sm:inline">Viewing:</span>
                <span data-testid="store-header-name">{store?.name || "Loading…"}</span>
                {canSwitch && <ChevronDown className="w-3.5 h-3.5 text-neutral-400"/>}
              </button>
              {switcherOpen && canSwitch && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl border border-neutral-200 shadow-lg py-1 z-50 max-h-80 overflow-y-auto" data-testid="location-selector-menu">
                  {isAdmin && <><button onClick={() => { setSwitcherOpen(false); nav("/admin"); }} className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 font-medium">All Stores</button><div className="border-t border-neutral-100 my-1"/></>}
                  {stores.map((s) => (
                    <button key={s.id} onClick={() => { setSwitcherOpen(false); nav(`/admin/stores/${s.id}`); }} className={`w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between ${s.id === sid ? "text-[hsl(var(--primary))] font-semibold" : ""}`} data-testid={`location-item-${s.id}`}>
                      <span>{s.name}</span><span className="text-[10px] text-neutral-400">{s.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="text-xs text-neutral-500 hidden sm:block">{user?.name} · <span className="capitalize">{user?.role?.replace("_", " ")}</span></div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
